// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================

import React, { useState, useRef, useCallback } from 'react';

const INDUSTRIES = [
  'Agriculture', 'Auto Dealership', 'Beauty', 'Cannabis', 'Cattle',
  'Construction', 'Directional Drilling', 'Event Venue', 'Fleet / Logistics',
  'Healthcare', 'Hoist / Crane', 'Insurance', 'Iron / Metal', 'Mining',
  'Mechanic', 'Nursing', 'Oil & Gas', 'Overhead Doors', 'Paperwork / Forms',
  'Real Estate', 'Rig Operations', 'Service Control', 'Tire Shop',
  'Vac Hauling', 'Wellness', 'Custom',
];

const LOGO_STYLES = [
  { key: 'monogram', label: 'Monogram', desc: 'Initials in a shape', icon: 'Aa' },
  { key: 'wordmark', label: 'Wordmark', desc: 'Stylized text only', icon: 'Tt' },
  { key: 'icon_text', label: 'Icon + Text', desc: 'Icon left, text right', icon: '[] T' },
  { key: 'badge', label: 'Badge / Emblem', desc: 'Circular or shield with text', icon: '(T)' },
  { key: 'stacked', label: 'Stacked', desc: 'Icon on top, text below', icon: '[]T' },
  { key: 'minimal', label: 'Minimal', desc: 'Clean single symbol', icon: 'o' },
];

const PRESET_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#06b6d4', '#f97316',
  '#14b8a6', '#84cc16', '#e11d48', '#0ea5e9',
  '#a855f7', '#22c55e', '#d97706', '#64748b',
];

const SHAPES = [
  { key: 'circle', label: 'Circle' },
  { key: 'square', label: 'Square' },
  { key: 'rounded', label: 'Rounded Square' },
  { key: 'hexagon', label: 'Hexagon' },
  { key: 'shield', label: 'Shield' },
  { key: 'diamond', label: 'Diamond' },
];

const ICONS = [
  { key: 'wrench', emoji: '\u{1F527}', label: 'Wrench' },
  { key: 'truck', emoji: '\u{1F69A}', label: 'Truck' },
  { key: 'leaf', emoji: '\u{1F33F}', label: 'Leaf' },
  { key: 'medical', emoji: '\u{1FA7A}', label: 'Medical' },
  { key: 'building', emoji: '\u{1F3E2}', label: 'Building' },
  { key: 'gear', emoji: '\u2699\uFE0F', label: 'Gear' },
  { key: 'chart', emoji: '\u{1F4CA}', label: 'Chart' },
  { key: 'shield', emoji: '\u{1F6E1}\uFE0F', label: 'Shield' },
  { key: 'hammer', emoji: '\u{1F528}', label: 'Hammer' },
  { key: 'flame', emoji: '\u{1F525}', label: 'Flame' },
  { key: 'lightning', emoji: '\u26A1', label: 'Lightning' },
  { key: 'star', emoji: '\u2B50', label: 'Star' },
  { key: 'crown', emoji: '\u{1F451}', label: 'Crown' },
  { key: 'mountain', emoji: '\u{1F3D4}\uFE0F', label: 'Mountain' },
  { key: 'wave', emoji: '\u{1F30A}', label: 'Wave' },
  { key: 'sun', emoji: '\u2600\uFE0F', label: 'Sun' },
  { key: 'compass', emoji: '\u{1F9ED}', label: 'Compass' },
  { key: 'target', emoji: '\u{1F3AF}', label: 'Target' },
  { key: 'rocket', emoji: '\u{1F680}', label: 'Rocket' },
  { key: 'briefcase', emoji: '\u{1F4BC}', label: 'Briefcase' },
  { key: 'plant', emoji: '\u{1F331}', label: 'Seedling' },
  { key: 'home', emoji: '\u{1F3E0}', label: 'Home' },
  { key: 'car', emoji: '\u{1F697}', label: 'Car' },
  { key: 'cow', emoji: '\u{1F404}', label: 'Cow' },
  { key: 'pill', emoji: '\u{1F48A}', label: 'Pill' },
  { key: 'money', emoji: '\u{1F4B0}', label: 'Money' },
  { key: 'palette', emoji: '\u{1F3A8}', label: 'Palette' },
  { key: 'key', emoji: '\u{1F511}', label: 'Key' },
  { key: 'lock', emoji: '\u{1F512}', label: 'Lock' },
  { key: 'diamond', emoji: '\u{1F48E}', label: 'Diamond' },
  { key: 'trophy', emoji: '\u{1F3C6}', label: 'Trophy' },
  { key: 'link', emoji: '\u{1F517}', label: 'Link' },
  { key: 'globe', emoji: '\u{1F310}', label: 'Globe' },
  { key: 'bolt', emoji: '\u{1F529}', label: 'Bolt' },
  { key: 'eye', emoji: '\u{1F441}\uFE0F', label: 'Eye' },
  { key: 'phone', emoji: '\u{1F4F1}', label: 'Phone' },
  { key: 'computer', emoji: '\u{1F4BB}', label: 'Computer' },
  { key: 'wheat', emoji: '\u{1F33E}', label: 'Wheat' },
  { key: 'construction', emoji: '\u{1F3D7}\uFE0F', label: 'Construction' },
  { key: 'oil', emoji: '\u{1F6E2}\uFE0F', label: 'Oil Drum' },
  { key: 'cannabis', emoji: '\u{1F343}', label: 'Cannabis' },
  { key: 'tire', emoji: '\u{1F6DE}', label: 'Tire' },
  { key: 'heart', emoji: '\u2764\uFE0F', label: 'Heart' },
  { key: 'sparkle', emoji: '\u2728', label: 'Sparkle' },
];

const FONT_STYLES = [
  { key: 'bold', label: 'Bold', family: 'Arial, Helvetica, sans-serif', weight: '800' },
  { key: 'clean', label: 'Clean', family: "'Segoe UI', Tahoma, sans-serif", weight: '600' },
  { key: 'elegant', label: 'Elegant', family: 'Georgia, "Times New Roman", serif', weight: '400' },
  { key: 'industrial', label: 'Industrial', family: '"Impact", "Arial Black", sans-serif', weight: '700' },
  { key: 'modern', label: 'Modern', family: '"Trebuchet MS", "Lucida Sans", sans-serif', weight: '500' },
  { key: 'rounded', label: 'Rounded', family: '"Verdana", "Geneva", sans-serif', weight: '600' },
];

const BG_OPTIONS = [
  { key: 'transparent', label: 'Transparent' },
  { key: 'solid', label: 'Solid Color' },
  { key: 'gradient', label: 'Gradient' },
];

const EXPORT_SIZES = [64, 128, 256, 512, 1024];

const TABS = [
  { key: 'info', label: '1. Business Info' },
  { key: 'style', label: '2. Style' },
  { key: 'customize', label: '3. Customize' },
  { key: 'export', label: '4. Export' },
];

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  } : { r: 99, g: 102, b: 241 };
}

function lighten(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const lr = Math.min(255, r + Math.round((255 - r) * amount));
  const lg = Math.min(255, g + Math.round((255 - g) * amount));
  const lb = Math.min(255, b + Math.round((255 - b) * amount));
  return `rgb(${lr},${lg},${lb})`;
}

function darken(hex, amount) {
  const { r, g, b } = hexToRgb(hex);
  const dr = Math.max(0, Math.round(r * (1 - amount)));
  const dg = Math.max(0, Math.round(g * (1 - amount)));
  const db = Math.max(0, Math.round(b * (1 - amount)));
  return `rgb(${dr},${dg},${db})`;
}

function getInitials(name) {
  if (!name) return 'MT';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

function getShapePath(shape, cx, cy, size) {
  const r = size / 2;
  switch (shape) {
    case 'circle':
      return `<circle cx="${cx}" cy="${cy}" r="${r}" />`;
    case 'square':
      return `<rect x="${cx - r}" y="${cy - r}" width="${size}" height="${size}" />`;
    case 'rounded':
      return `<rect x="${cx - r}" y="${cy - r}" width="${size}" height="${size}" rx="${size * 0.15}" ry="${size * 0.15}" />`;
    case 'hexagon': {
      const pts = [];
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        pts.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
      }
      return `<polygon points="${pts.join(' ')}" />`;
    }
    case 'shield':
      return `<path d="${`M${cx} ${cy - r} L${cx + r * 0.85} ${cy - r * 0.55} L${cx + r * 0.85} ${cy + r * 0.15} Q${cx + r * 0.85} ${cy + r} ${cx} ${cy + r} Q${cx - r * 0.85} ${cy + r} ${cx - r * 0.85} ${cy + r * 0.15} L${cx - r * 0.85} ${cy - r * 0.55} Z`}" />`;
    case 'diamond':
      return `<polygon points="${cx},${cy - r} ${cx + r * 0.75},${cy} ${cx},${cy + r} ${cx - r * 0.75},${cy}" />`;
    default:
      return `<circle cx="${cx}" cy="${cy}" r="${r}" />`;
  }
}

function generateLogoSVG(config) {
  const {
    businessName = 'My Business',
    tagline = '',
    logoStyle = 'icon_text',
    primaryColor = '#6366f1',
    secondaryColor = '#1a1a2e',
    shape = 'rounded',
    selectedIcon = null,
    fontStyle = 'bold',
    bgOption = 'transparent',
  } = config;

  const font = FONT_STYLES.find(f => f.key === fontStyle) || FONT_STYLES[0];
  const iconObj = ICONS.find(i => i.key === selectedIcon);
  const iconEmoji = iconObj ? iconObj.emoji : '\u2B50';
  const initials = getInitials(businessName);
  const displayName = businessName || 'My Business';

  const svgW = 512;
  const svgH = 512;

  let bgFill = '';
  if (bgOption === 'solid') {
    bgFill = `<rect width="${svgW}" height="${svgH}" fill="${secondaryColor}" />`;
  } else if (bgOption === 'gradient') {
    bgFill = `<defs><linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:${secondaryColor};stop-opacity:1" /><stop offset="100%" style="stop-color:${darken(secondaryColor, 0.3)};stop-opacity:1" /></linearGradient></defs><rect width="${svgW}" height="${svgH}" fill="url(#bgGrad)" />`;
  }

  let content = '';

  switch (logoStyle) {
    case 'monogram': {
      const cx = svgW / 2;
      const cy = svgH / 2;
      const shapeSize = 320;
      const shapeSvg = getShapePath(shape, cx, cy, shapeSize);
      content = `
        <g fill="${primaryColor}">${shapeSvg}</g>
        <text x="${cx}" y="${cy + 8}" text-anchor="middle" dominant-baseline="central"
          font-family="${font.family}" font-weight="${font.weight}" font-size="140"
          fill="#ffffff" letter-spacing="4">${initials}</text>
      `;
      break;
    }

    case 'wordmark': {
      const cx = svgW / 2;
      const nameY = tagline ? svgH / 2 - 20 : svgH / 2;
      const fontSize = Math.min(80, 360 / Math.max(displayName.length, 1) * 1.4);
      content = `
        <text x="${cx}" y="${nameY}" text-anchor="middle" dominant-baseline="central"
          font-family="${font.family}" font-weight="${font.weight}" font-size="${fontSize}"
          fill="${primaryColor}" letter-spacing="2">${displayName}</text>
        ${tagline ? `<text x="${cx}" y="${nameY + fontSize * 0.6 + 12}" text-anchor="middle" dominant-baseline="central"
          font-family="${font.family}" font-weight="400" font-size="${fontSize * 0.3}"
          fill="${secondaryColor}" letter-spacing="3" opacity="0.7">${tagline.toUpperCase()}</text>` : ''}
      `;
      break;
    }

    case 'icon_text': {
      const shapeSize = 200;
      const shapeCx = 140;
      const shapeCy = svgH / 2;
      const shapeSvg = getShapePath(shape, shapeCx, shapeCy, shapeSize);
      const fontSize = Math.min(60, 240 / Math.max(displayName.length, 1) * 1.5);
      const textX = 270;
      const nameY = tagline ? shapeCy - 18 : shapeCy;
      content = `
        <g fill="${primaryColor}">${shapeSvg}</g>
        <text x="${shapeCx}" y="${shapeCy + 4}" text-anchor="middle" dominant-baseline="central"
          font-size="72" fill="#ffffff">${iconEmoji}</text>
        <text x="${textX}" y="${nameY}" dominant-baseline="central"
          font-family="${font.family}" font-weight="${font.weight}" font-size="${fontSize}"
          fill="${primaryColor}" letter-spacing="1">${displayName}</text>
        ${tagline ? `<text x="${textX}" y="${nameY + fontSize * 0.65 + 8}" dominant-baseline="central"
          font-family="${font.family}" font-weight="400" font-size="${fontSize * 0.35}"
          fill="${secondaryColor}" letter-spacing="2" opacity="0.6">${tagline}</text>` : ''}
      `;
      break;
    }

    case 'badge': {
      const cx = svgW / 2;
      const cy = svgH / 2;
      const outerR = 220;
      const innerR = 185;
      content = `
        <circle cx="${cx}" cy="${cy}" r="${outerR}" fill="${primaryColor}" />
        <circle cx="${cx}" cy="${cy}" r="${innerR}" fill="none" stroke="#ffffff" stroke-width="3" opacity="0.4" />
        <text x="${cx}" y="${cy - 30}" text-anchor="middle" dominant-baseline="central"
          font-size="64" fill="#ffffff">${iconEmoji}</text>
        <text x="${cx}" y="${cy + 50}" text-anchor="middle" dominant-baseline="central"
          font-family="${font.family}" font-weight="${font.weight}" font-size="${Math.min(44, 280 / Math.max(displayName.length, 1) * 1.4)}"
          fill="#ffffff" letter-spacing="2">${displayName}</text>
        ${tagline ? `<text x="${cx}" y="${cy + 90}" text-anchor="middle" dominant-baseline="central"
          font-family="${font.family}" font-weight="400" font-size="16"
          fill="#ffffff" letter-spacing="3" opacity="0.75">${tagline.toUpperCase()}</text>` : ''}
        <path d="M${cx - 80} ${cy + 65} L${cx + 80} ${cy + 65}" stroke="#ffffff" stroke-width="1.5" opacity="0.35" />
      `;
      break;
    }

    case 'stacked': {
      const cx = svgW / 2;
      const shapeSize = 200;
      const shapeCy = svgH / 2 - 60;
      const shapeSvg = getShapePath(shape, cx, shapeCy, shapeSize);
      const fontSize = Math.min(52, 380 / Math.max(displayName.length, 1) * 1.4);
      const textY = shapeCy + shapeSize / 2 + 50;
      content = `
        <g fill="${primaryColor}">${shapeSvg}</g>
        <text x="${cx}" y="${shapeCy + 4}" text-anchor="middle" dominant-baseline="central"
          font-size="72" fill="#ffffff">${iconEmoji}</text>
        <text x="${cx}" y="${textY}" text-anchor="middle" dominant-baseline="central"
          font-family="${font.family}" font-weight="${font.weight}" font-size="${fontSize}"
          fill="${primaryColor}" letter-spacing="2">${displayName}</text>
        ${tagline ? `<text x="${cx}" y="${textY + fontSize * 0.6 + 8}" text-anchor="middle" dominant-baseline="central"
          font-family="${font.family}" font-weight="400" font-size="${fontSize * 0.32}"
          fill="${secondaryColor}" letter-spacing="3" opacity="0.6">${tagline.toUpperCase()}</text>` : ''}
      `;
      break;
    }

    case 'minimal': {
      const cx = svgW / 2;
      const cy = svgH / 2;
      const shapeSize = 280;
      const shapeSvg = getShapePath(shape, cx, cy, shapeSize);
      content = `
        <g fill="${primaryColor}" opacity="0.1">${shapeSvg}</g>
        <text x="${cx}" y="${cy + 8}" text-anchor="middle" dominant-baseline="central"
          font-size="120" fill="${primaryColor}">${iconEmoji}</text>
      `;
      break;
    }

    default:
      break;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgW} ${svgH}" width="${svgW}" height="${svgH}">
${bgFill}
${content}
</svg>`;
}

export default function LogoGeneratorPage() {
  const [activeTab, setActiveTab] = useState('info');
  const [logosGenerated, setLogosGenerated] = useState(0);

  // Step 1: Business Info
  const [businessName, setBusinessName] = useState('');
  const [tagline, setTagline] = useState('');
  const [industry, setIndustry] = useState('');

  // Step 2: Style
  const [logoStyle, setLogoStyle] = useState('icon_text');

  // Step 3: Customize
  const [primaryColor, setPrimaryColor] = useState('#6366f1');
  const [secondaryColor, setSecondaryColor] = useState('#1a1a2e');
  const [customPrimary, setCustomPrimary] = useState('');
  const [customSecondary, setCustomSecondary] = useState('');
  const [shape, setShape] = useState('rounded');
  const [selectedIcon, setSelectedIcon] = useState('star');
  const [fontStyle, setFontStyle] = useState('bold');
  const [bgOption, setBgOption] = useState('transparent');

  // Step 4: Export
  const [exportSize, setExportSize] = useState(512);
  const [previewBg, setPreviewBg] = useState('white');
  const [copied, setCopied] = useState(false);

  const canvasRef = useRef(null);

  const logoConfig = {
    businessName: businessName || 'My Business',
    tagline,
    logoStyle,
    primaryColor,
    secondaryColor,
    shape,
    selectedIcon,
    fontStyle,
    bgOption,
  };

  const svgString = generateLogoSVG(logoConfig);

  const handlePrimaryColorSelect = (color) => {
    setPrimaryColor(color);
    setCustomPrimary('');
  };

  const handleCustomPrimaryChange = (e) => {
    const val = e.target.value;
    setCustomPrimary(val);
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      setPrimaryColor(val);
    }
  };

  const handleSecondaryColorSelect = (color) => {
    setSecondaryColor(color);
    setCustomSecondary('');
  };

  const handleCustomSecondaryChange = (e) => {
    const val = e.target.value;
    setCustomSecondary(val);
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      setSecondaryColor(val);
    }
  };

  const downloadSVG = useCallback(() => {
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(businessName || 'logo').replace(/\s+/g, '-').toLowerCase()}-logo.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setLogosGenerated(prev => prev + 1);
  }, [svgString, businessName]);

  const downloadPNG = useCallback(() => {
    const canvas = document.createElement('canvas');
    canvas.width = exportSize;
    canvas.height = exportSize;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      if (bgOption === 'transparent') {
        ctx.clearRect(0, 0, exportSize, exportSize);
      }
      ctx.drawImage(img, 0, 0, exportSize, exportSize);
      URL.revokeObjectURL(url);
      const pngUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = `${(businessName || 'logo').replace(/\s+/g, '-').toLowerCase()}-logo-${exportSize}px.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setLogosGenerated(prev => prev + 1);
    };

    img.src = url;
  }, [svgString, exportSize, businessName, bgOption]);

  const copySVGCode = useCallback(() => {
    navigator.clipboard.writeText(svgString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fallback copy
      const textarea = document.createElement('textarea');
      textarea.value = svgString;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [svgString]);

  // ---------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'auto',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-muted)',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };

  const btnPrimary = {
    padding: '10px 20px',
    background: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s ease',
  };

  const btnSecondary = {
    padding: '10px 20px',
    background: 'var(--bg)',
    color: 'var(--text)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s ease',
  };

  // ---------------------------------------------------------------
  // Tab renderers
  // ---------------------------------------------------------------

  const renderInfoTab = () => (
    <div>
      <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
        Business Info
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
        Enter your business name and industry to get started.
      </p>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Business Name <span style={{ color: '#ef4444' }}>*</span></label>
        <input
          type="text"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="e.g. Summit Drilling Co."
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Tagline / Subtitle (Optional)</label>
        <input
          type="text"
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder="e.g. Precision Directional Services"
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Industry</label>
        <select
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          style={selectStyle}
        >
          <option value="">Select an industry...</option>
          {INDUSTRIES.map(ind => (
            <option key={ind} value={ind}>{ind}</option>
          ))}
        </select>
      </div>

      <button
        onClick={() => setActiveTab('style')}
        disabled={!businessName.trim()}
        style={{
          ...btnPrimary,
          opacity: businessName.trim() ? 1 : 0.5,
          cursor: businessName.trim() ? 'pointer' : 'not-allowed',
        }}
      >
        Continue to Style
      </button>
    </div>
  );

  const renderStyleTab = () => (
    <div>
      <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
        Logo Style
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
        Choose a layout style for your logo.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: 12,
        marginBottom: 20,
      }}>
        {LOGO_STYLES.map(s => {
          const isSelected = logoStyle === s.key;
          return (
            <div
              key={s.key}
              onClick={() => setLogoStyle(s.key)}
              style={{
                padding: 16,
                borderRadius: 12,
                cursor: 'pointer',
                background: 'var(--card)',
                border: isSelected ? '2px solid #6366f1' : '2px solid var(--border)',
                boxShadow: isSelected ? '0 0 0 3px rgba(99,102,241,0.2)' : 'none',
                transition: 'all 0.15s ease',
                textAlign: 'center',
              }}
            >
              <div style={{
                fontSize: 20,
                fontWeight: 700,
                color: isSelected ? '#6366f1' : 'var(--text-muted)',
                marginBottom: 6,
                fontFamily: 'monospace',
                letterSpacing: -1,
              }}>
                {s.icon}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
                {s.label}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {s.desc}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => setActiveTab('info')} style={btnSecondary}>Back</button>
        <button onClick={() => setActiveTab('customize')} style={btnPrimary}>Continue to Customize</button>
      </div>
    </div>
  );

  const renderCustomizeTab = () => (
    <div>
      <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
        Customize
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
        Fine-tune colors, shape, icon, and font.
      </p>

      {/* Primary Color */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Primary Color</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          {PRESET_COLORS.map(c => (
            <div
              key={c}
              onClick={() => handlePrimaryColorSelect(c)}
              style={{
                width: 28, height: 28, borderRadius: 6, background: c, cursor: 'pointer',
                border: primaryColor === c ? '3px solid #fff' : '2px solid transparent',
                boxShadow: primaryColor === c ? `0 0 0 2px ${c}` : 'none',
                transition: 'all 0.15s ease', flexShrink: 0,
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={customPrimary}
            onChange={handleCustomPrimaryChange}
            placeholder="#hex"
            style={{ ...inputStyle, width: 120 }}
          />
          <input
            type="color"
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            style={{ width: 32, height: 32, border: 'none', cursor: 'pointer', background: 'none', padding: 0 }}
          />
          <div style={{
            width: 28, height: 28, borderRadius: 6, background: primaryColor,
            border: '1px solid var(--border)', flexShrink: 0,
          }} />
        </div>
      </div>

      {/* Secondary Color */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Secondary / Text Color</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {['#1a1a2e', '#0f172a', '#1e293b', '#374151', '#111827', '#ffffff'].map(c => (
            <div
              key={c}
              onClick={() => handleSecondaryColorSelect(c)}
              style={{
                width: 28, height: 28, borderRadius: 6, background: c, cursor: 'pointer',
                border: secondaryColor === c ? '3px solid #6366f1' : `2px solid ${c === '#ffffff' ? '#ccc' : 'transparent'}`,
                boxShadow: secondaryColor === c ? '0 0 0 2px rgba(99,102,241,0.4)' : 'none',
                transition: 'all 0.15s ease', flexShrink: 0,
              }}
            />
          ))}
          <input
            type="text"
            value={customSecondary}
            onChange={handleCustomSecondaryChange}
            placeholder="#hex"
            style={{ ...inputStyle, width: 100 }}
          />
          <input
            type="color"
            value={secondaryColor}
            onChange={(e) => setSecondaryColor(e.target.value)}
            style={{ width: 32, height: 32, border: 'none', cursor: 'pointer', background: 'none', padding: 0 }}
          />
        </div>
      </div>

      {/* Shape Selector */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Shape</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {SHAPES.map(s => (
            <button
              key={s.key}
              onClick={() => setShape(s.key)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: shape === s.key ? '2px solid #6366f1' : '1px solid var(--border)',
                background: shape === s.key ? 'rgba(99,102,241,0.08)' : 'var(--card)',
                color: shape === s.key ? '#6366f1' : 'var(--text)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Icon Selector */}
      {logoStyle !== 'monogram' && logoStyle !== 'wordmark' && (
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Icon</label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
            gap: 6,
            maxHeight: 220,
            overflowY: 'auto',
            padding: 4,
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'var(--bg)',
          }}>
            {ICONS.map(ic => (
              <div
                key={ic.key}
                onClick={() => setSelectedIcon(ic.key)}
                title={ic.label}
                style={{
                  width: 40, height: 40,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, borderRadius: 6, cursor: 'pointer',
                  background: selectedIcon === ic.key ? 'rgba(99,102,241,0.15)' : 'transparent',
                  border: selectedIcon === ic.key ? '2px solid #6366f1' : '2px solid transparent',
                  transition: 'all 0.1s ease',
                }}
              >
                {ic.emoji}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Font Style */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Font Style</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {FONT_STYLES.map(f => (
            <button
              key={f.key}
              onClick={() => setFontStyle(f.key)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: fontStyle === f.key ? '2px solid #6366f1' : '1px solid var(--border)',
                background: fontStyle === f.key ? 'rgba(99,102,241,0.08)' : 'var(--card)',
                color: fontStyle === f.key ? '#6366f1' : 'var(--text)',
                fontSize: 13,
                fontWeight: f.weight,
                fontFamily: f.family,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Background */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Background</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {BG_OPTIONS.map(b => (
            <button
              key={b.key}
              onClick={() => setBgOption(b.key)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: bgOption === b.key ? '2px solid #6366f1' : '1px solid var(--border)',
                background: bgOption === b.key ? 'rgba(99,102,241,0.08)' : 'var(--card)',
                color: bgOption === b.key ? '#6366f1' : 'var(--text)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => setActiveTab('style')} style={btnSecondary}>Back</button>
        <button onClick={() => setActiveTab('export')} style={btnPrimary}>Continue to Export</button>
      </div>
    </div>
  );

  const renderExportTab = () => (
    <div>
      <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
        Preview & Export
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
        Preview your logo on different backgrounds and export in your preferred format.
      </p>

      {/* Preview Background Switcher */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Preview Background</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { key: 'white', label: 'White', bg: '#ffffff' },
            { key: 'dark', label: 'Dark', bg: '#1a1a2e' },
            { key: 'card', label: 'Card Mockup', bg: 'card' },
            { key: 'bcard', label: 'Business Card', bg: 'bcard' },
          ].map(p => (
            <button
              key={p.key}
              onClick={() => setPreviewBg(p.key)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: previewBg === p.key ? '2px solid #6366f1' : '1px solid var(--border)',
                background: previewBg === p.key ? 'rgba(99,102,241,0.08)' : 'var(--card)',
                color: previewBg === p.key ? '#6366f1' : 'var(--text)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Export Size */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Export Size (PNG)</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {EXPORT_SIZES.map(size => (
            <button
              key={size}
              onClick={() => setExportSize(size)}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: exportSize === size ? '2px solid #6366f1' : '1px solid var(--border)',
                background: exportSize === size ? 'rgba(99,102,241,0.08)' : 'var(--card)',
                color: exportSize === size ? '#6366f1' : 'var(--text)',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {size}px
            </button>
          ))}
        </div>
      </div>

      {/* Export Buttons */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        <button onClick={downloadSVG} style={btnPrimary}>
          Download SVG
        </button>
        <button onClick={downloadPNG} style={btnPrimary}>
          Download PNG ({exportSize}px)
        </button>
        <button onClick={copySVGCode} style={btnSecondary}>
          {copied ? 'Copied!' : 'Copy SVG Code'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => setActiveTab('customize')} style={btnSecondary}>Back</button>
      </div>
    </div>
  );

  // ---------------------------------------------------------------
  // Preview panel
  // ---------------------------------------------------------------

  const renderPreview = () => {
    let bgStyle = {};
    let mockupContent = null;

    if (previewBg === 'white') {
      bgStyle = { background: '#ffffff' };
    } else if (previewBg === 'dark') {
      bgStyle = { background: '#1a1a2e' };
    } else if (previewBg === 'card') {
      bgStyle = { background: '#f3f4f6' };
      mockupContent = (
        <div style={{
          width: '100%',
          maxWidth: 300,
          background: '#ffffff',
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          padding: 28,
          textAlign: 'center',
        }}>
          <div
            dangerouslySetInnerHTML={{ __html: svgString }}
            style={{ width: '100%', maxWidth: 180, margin: '0 auto 16px' }}
          />
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>
            {businessName || 'My Business'}
          </div>
          {tagline && (
            <div style={{ fontSize: 12, color: '#6b7280' }}>{tagline}</div>
          )}
        </div>
      );
    } else if (previewBg === 'bcard') {
      bgStyle = { background: '#e5e7eb' };
      mockupContent = (
        <div style={{
          width: '100%',
          maxWidth: 340,
          aspectRatio: '3.5 / 2',
          background: '#ffffff',
          borderRadius: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}>
          <div>
            <div
              dangerouslySetInnerHTML={{ __html: svgString }}
              style={{ width: 80, marginBottom: 12 }}
            />
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>
              {businessName || 'My Business'}
            </div>
            {tagline && (
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{tagline}</div>
            )}
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 10, marginTop: 10 }}>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>www.example.com</div>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>contact@example.com</div>
          </div>
        </div>
      );
    }

    return (
      <div style={{
        ...bgStyle,
        borderRadius: 12,
        border: '1px solid var(--border)',
        minHeight: 380,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        transition: 'background 0.2s ease',
      }}>
        {mockupContent || (
          <div
            dangerouslySetInnerHTML={{ __html: svgString }}
            style={{ width: '100%', maxWidth: 280 }}
          />
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------
  return (
    <div className="page-body" style={{ padding: 24 }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 28 }}>
        <h1 className="page-title" style={{ color: 'var(--text)', fontSize: 26, fontWeight: 700, margin: '0 0 6px' }}>
          Logo Generator
        </h1>
        <p className="page-subtitle" style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
          Design a professional logo for your business in minutes. Fully client-side, no AI required.
        </p>
      </div>

      {/* Two-Column Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: typeof window !== 'undefined' && window.innerWidth > 900 ? '3fr 2fr' : '1fr',
        gap: 20,
        marginBottom: 28,
      }}>
        {/* Left Panel: Configurator */}
        <div style={{
          padding: 24,
          background: 'var(--card)',
          borderRadius: 12,
          border: '1px solid var(--border)',
        }}>
          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            gap: 0,
            marginBottom: 24,
            borderBottom: '2px solid var(--border)',
          }}>
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === tab.key ? '2px solid #6366f1' : '2px solid transparent',
                  color: activeTab === tab.key ? '#6366f1' : 'var(--text-muted)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  marginBottom: -2,
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'info' && renderInfoTab()}
          {activeTab === 'style' && renderStyleTab()}
          {activeTab === 'customize' && renderCustomizeTab()}
          {activeTab === 'export' && renderExportTab()}
        </div>

        {/* Right Panel: Live Preview */}
        <div style={{
          position: typeof window !== 'undefined' && window.innerWidth > 900 ? 'sticky' : 'static',
          top: 24,
          alignSelf: 'start',
        }}>
          <div style={{
            padding: 20,
            background: 'var(--card)',
            borderRadius: 12,
            border: '1px solid var(--border)',
          }}>
            <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>
              Live Preview
            </h2>
            {renderPreview()}

            {/* Quick info under preview */}
            <div style={{
              marginTop: 14,
              padding: 12,
              background: 'var(--bg)',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Style
                </span>
                <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500 }}>
                  {LOGO_STYLES.find(s => s.key === logoStyle)?.label || logoStyle}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Shape
                </span>
                <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500 }}>
                  {SHAPES.find(s => s.key === shape)?.label || shape}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Colors
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, background: primaryColor, border: '1px solid var(--border)' }} />
                  <div style={{ width: 16, height: 16, borderRadius: 4, background: secondaryColor, border: '1px solid var(--border)' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        padding: '20px 24px',
        background: 'var(--card)',
        borderRadius: 12,
        border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            padding: '6px 14px',
            background: 'rgba(99,102,241,0.08)',
            borderRadius: 20,
            fontSize: 13,
            fontWeight: 600,
            color: '#6366f1',
          }}>
            Logos exported: {logosGenerated}
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Need a professional custom logo? Contact{' '}
          <a
            href="mailto:investor@miskulintechnologies.com"
            style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}
          >
            investor@miskulintechnologies.com
          </a>
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}
